import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  fetchMilestoneBanners,
  acknowledgeMilestoneBanner,
} from '../notifications/milestoneBannerClient';
import type { MilestoneBannerItem } from '../types/models/MilestoneBanner';

/**
 * Homepage milestone-banner store — Story 76.3a.
 *
 * Backs the celebration banner stack that sits above the *"What needs your
 * attention"* notification list. The BE owns visibility, filtering, ordering
 * (ORDER BY EffectiveEndDate DESC, MilestoneId DESC), and the per-response
 * `kudosUrl` — the FE just renders what it receives.
 *
 * Pattern mirrors `homepageNotificationFeedStore`:
 *   - Single shared SSE connection (Story 76.2a) → `scheduleReload` (200 ms
 *     debounced) → `load(tenantId)` → BE round-trip.
 *   - Stale-fetch protection: the in-flight request's tenantId is compared
 *     against the current state on resolve; a tenant switch mid-flight
 *     discards the late response.
 *   - Reload timer lives in the store-creator closure (not module scope) so
 *     HMR resets cleanly and the timer cannot be touched externally.
 *
 * Dismiss flow (AC 5): `acknowledge(milestoneId)` optimistically removes the
 * banner from `items`. On request failure the banner is restored at its
 * original index and `error` is set to a non-blocking message.
 *
 * Module Federation rule: import via the absolute path
 * `@helios/shared-domain/stores/homepageMilestoneBannerStore` (never relative).
 */

const RELOAD_DEBOUNCE_MS = 200;

export interface HomepageMilestoneBannerStore {
  items: MilestoneBannerItem[];
  /** BE-resolved Hibob deep-link (null = button hidden even for internal users). */
  kudosUrl: string | null;
  isLoading: boolean;
  error: string | null;
  tenantId: string | null;

  load: (tenantId: string) => Promise<void>;
  /**
   * Coalesces bursts of SSE events into a single re-fetch against the
   * currently-stored `tenantId`. No-op when the store has not been seeded yet.
   */
  scheduleReload: () => void;
  /** Optimistically dismiss + POST acknowledge; restore on failure. */
  acknowledge: (milestoneId: string) => Promise<void>;
  reset: () => void;
}

const initialState = {
  items: [] as MilestoneBannerItem[],
  kudosUrl: null as string | null,
  isLoading: false,
  error: null as string | null,
  tenantId: null as string | null,
};

export const useHomepageMilestoneBannerStore = create<HomepageMilestoneBannerStore>()(
  devtools(
    (set, get) => {
      // Closure-scoped to keep HMR clean and prevent any external file from
      // touching the timer directly (76.2a's QA refactor pattern).
      let reloadTimer: ReturnType<typeof setTimeout> | null = null;

      return {
        ...initialState,

        load: async (tenantId: string) => {
          set(
            { isLoading: true, error: null, tenantId },
            false,
            'homepageMilestoneBanner/load/start'
          );
          try {
            const res = await fetchMilestoneBanners();
            if (get().tenantId !== tenantId) {
              // Tenant changed mid-flight — discard stale response.
              return;
            }
            set(
              {
                items: res.items,
                kudosUrl: res.kudosUrl,
                isLoading: false,
                error: null,
              },
              false,
              'homepageMilestoneBanner/load/success'
            );
          } catch (err) {
            if (get().tenantId !== tenantId) return;
            set(
              {
                isLoading: false,
                error: err instanceof Error ? err.message : 'Failed to load milestone banners',
              },
              false,
              'homepageMilestoneBanner/load/error'
            );
          }
        },

        scheduleReload: () => {
          const { tenantId } = get();
          if (!tenantId) return;
          if (reloadTimer !== null) clearTimeout(reloadTimer);
          reloadTimer = setTimeout(() => {
            reloadTimer = null;
            const current = get().tenantId;
            if (!current) return;
            void get().load(current);
          }, RELOAD_DEBOUNCE_MS);
        },

        acknowledge: async (milestoneId: string) => {
          const snapshot = get().items;
          const index = snapshot.findIndex((b) => b.milestoneId === milestoneId);
          if (index === -1) return;

          const optimisticItems = snapshot.filter((b) => b.milestoneId !== milestoneId);
          set(
            { items: optimisticItems, error: null },
            false,
            'homepageMilestoneBanner/acknowledge/optimistic'
          );

          try {
            await acknowledgeMilestoneBanner(milestoneId);
            // Re-fetch so a 4th candidate (if any) surfaces immediately —
            // the BE response is capped at 3 per request, so dismissing one
            // may free a slot for the next-most-recent unacknowledged
            // completion. Debounced so a burst of dismisses coalesces.
            get().scheduleReload();
          } catch (err) {
            // Restore at the original index — the BE call failed so the
            // banner is still the truth.
            const current = get().items;
            const restored = [...current];
            // Only re-insert if it's still absent (otherwise a parallel reload
            // already merged the new state).
            if (!restored.some((b) => b.milestoneId === milestoneId)) {
              restored.splice(Math.min(index, restored.length), 0, snapshot[index]);
            }
            set(
              {
                items: restored,
                error:
                  err instanceof Error ? err.message : 'Failed to acknowledge milestone banner',
              },
              false,
              'homepageMilestoneBanner/acknowledge/restore'
            );
          }
        },

        reset: () => {
          if (reloadTimer !== null) {
            clearTimeout(reloadTimer);
            reloadTimer = null;
          }
          set({ ...initialState }, false, 'homepageMilestoneBanner/reset');
        },
      };
    },
    { name: 'homepageMilestoneBannerStore' }
  )
);
